import { useEffect, useState } from "react";
import { toast } from "sonner";
import { euro } from "@/lib/store";
import {
  vModels, vCreateModel, vPatchModel, vDeleteModel,
  vBenchmarks, vAddBenchmark, vBenchmarkAlert, vBenchmarkHistory,
  vGetConfig, vPutConfig, vSimulate,
  vValuations, vValuationDetail, vPatchValuation, vVerify, vPurchase,
  V_STATUSES, V_STATUS_META,
} from "@/lib/valuation";
import { vBenchmarkCheck, vProposals, vApproveProposal, vRejectProposal } from "@/lib/valuation";
import { vAlgoVersions, vAlgoCreate, vAlgoPatch, vAlgoActivate, vAlgoArchive, vAlgoCompare, vBenchmarkCheckState } from "@/lib/valuation";

const SUB = [["agenda", "Agenda"], ["simulatore", "Simulatore"], ["curva", "Curva Svalutazione"], ["versioni", "Versioni Algoritmo"], ["aggiornamenti", "Aggiornamenti"], ["config", "Configurazione"]];

const Field = ({ label, children }) => (
  <label className="block text-xs text-zinc-400">{label}<div className="mt-1">{children}</div></label>
);
const inp = "w-full rounded-lg border border-white/10 bg-[#0B0E13] px-3 py-2 text-sm text-white outline-none focus:border-[#2563EB]";
const card = "rounded-xl border border-white/10 bg-[#11141A] p-4";

// ============================= AGENDA =============================
function Agenda() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("");
  const [sel, setSel] = useState(null);

  const load = () => vValuations(status ? { status } : {}).then(setRows).catch(() => {});
  useEffect(() => { load(); }, [status]); // eslint-disable-line

  return (
    <section className="space-y-4" data-testid="valuation-agenda">
      <div className="flex flex-wrap items-center gap-2">
        <button data-testid="vfilter-all" onClick={() => setStatus("")} className={`rounded-full px-3 py-1.5 text-xs ${!status ? "bg-[#2563EB] text-white" : "border border-white/10 text-zinc-300"}`}>Tutte</button>
        {V_STATUSES.map((s) => (
          <button key={s} onClick={() => setStatus(s)} className={`rounded-full px-3 py-1.5 text-xs ${status === s ? "bg-[#2563EB] text-white" : "border border-white/10 text-zinc-300"}`}>{V_STATUS_META[s]?.label || s}</button>
        ))}
      </div>
      <div className={card}>
        {rows.length === 0 && <p className="py-8 text-center text-sm text-zinc-500">Nessuna valutazione.</p>}
        {rows.length > 0 && (
          <table className="w-full text-left text-sm">
            <thead className="text-xs text-zinc-500"><tr><th className="p-2">Cliente</th><th className="p-2">Dispositivo</th><th className="p-2">Stima</th><th className="p-2">Stato</th><th className="p-2">Data</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`vrow-${r.id}`} onClick={() => vValuationDetail(r.id).then(setSel)} className="cursor-pointer border-t border-white/5 hover:bg-white/5">
                  <td className="p-2">{r.customer_name}<div className="text-xs text-zinc-500">{r.phone}</div></td>
                  <td className="p-2">{r.device_model}<div className="text-xs text-zinc-500">{r.storage}</div></td>
                  <td className="p-2 font-semibold">{r.estimated_value_cents != null ? euro(r.estimated_value_cents) : "—"}</td>
                  <td className="p-2"><span className={`rounded-full px-2 py-0.5 text-xs ${V_STATUS_META[r.valuation_status]?.cls || "bg-zinc-500/15 text-zinc-400"}`}>{V_STATUS_META[r.valuation_status]?.label || r.valuation_status}</span></td>
                  <td className="p-2 text-xs text-zinc-500">{r.issued_at ? new Date(r.issued_at).toLocaleDateString("it-IT") : ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {sel && <ValuationDetail v={sel} onClose={() => setSel(null)} onChange={(nv) => { setSel(nv); load(); }} />}
    </section>
  );
}

function ValuationDetail({ v, onClose, onChange }) {
  const [verify, setVerify] = useState({ result: "CONFORME", final_offer_cents: v.estimated_value_cents, reason: "", checklist: {} });
  const [purchase, setPurchase] = useState({ final_purchase_price_cents: v.estimated_value_cents, imei: "", serial: "", color: "", notes: "" });

  const setStatus = async (s) => { try { onChange(await vPatchValuation(v.id, { valuation_status: s })); toast.success("Stato aggiornato"); } catch { toast.error("Errore"); } };
  const doVerify = async () => { try { onChange(await vVerify(v.id, verify)); toast.success("Verifica salvata"); } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); } };
  const doPurchase = async () => { try { onChange(await vPurchase(v.id, purchase)); toast.success("Acquisto registrato"); } catch { toast.error("Errore"); } };

  const s = v.snapshot || {};
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 p-4" onClick={onClose}>
      <div className="my-8 w-full max-w-3xl space-y-4 rounded-2xl border border-white/10 bg-[#0B0E13] p-6" onClick={(e) => e.stopPropagation()} data-testid="valuation-detail">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-lg font-bold">{v.device_model} · {v.storage}</h3>
            <p className="text-xs text-zinc-500">{v.customer_name} · {v.phone} {v.email && `· ${v.email}`}</p>
          </div>
          <button onClick={onClose} className="rounded-full border border-white/10 px-3 py-1 text-sm text-zinc-300">Chiudi</button>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className={card}>
            <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Questionario</p>
            <pre className="whitespace-pre-wrap text-xs text-zinc-300">{JSON.stringify(v.answers, null, 1)}</pre>
          </div>
          <div className={card}>
            <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Calcolo (immutabile)</p>
            {v.eligible ? (
              <div className="space-y-1 text-xs text-zinc-300">
                <Row k="Benchmark USD" v={`$${s.value_usd ?? "—"}`} />
                <Row k="Moltiplicatore" v={s.benchmark_multiplier} />
                <Row k="Benchmark IPHIX" v={euro(s.benchmark_cents)} />
                <Row k={`Penalità estetica (${s.aesthetic_pct}%)`} v={`- ${euro(s.aesthetic_penalty_cents)}`} />
                <Row k={`Penalità batteria (${s.battery_pct}%)`} v={`- ${euro(s.battery_penalty_cents)}`} />
                <Row k="Costo batteria" v={`- ${euro(s.battery_cost_cents)}`} />
                <Row k="Batteria non orig." v={`- ${euro(s.battery_non_original_cents)}`} />
                <Row k={`Riserva rischio (${s.risk_reserve_pct}%)`} v={`- ${euro(s.risk_reserve_cents)}`} />
                <Row k={`Margine IPHIX (${s.margin_pct}%)`} v={`- ${euro(s.margin_cents)}`} />
                <div className="mt-1 border-t border-white/10 pt-1"><Row k="Prezzo finale" v={euro(v.estimated_value_cents)} bold /></div>
                <p className="mt-1 text-[10px] text-zinc-500">Algo {v.algorithm_version} · Benchmark v{v.benchmark_version} · Scad. {v.expires_at ? new Date(v.expires_at).toLocaleDateString("it-IT") : "—"}</p>
              </div>
            ) : (
              <p className="text-xs text-rose-400">NON ACQUISTABILE — {(v.not_eligible_reasons || []).join(", ")}</p>
            )}
          </div>
        </div>

        {/* Azioni stato */}
        <div className={card}>
          <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Azioni</p>
          <div className="flex flex-wrap gap-2">
            {["CONFERMATO", "RIPROGRAMMATO", "PRESENTATO", "ANNULLATO", "NON_ACQUISTATO", "MANCATO_APPUNTAMENTO"].map((s2) => (
              <button key={s2} data-testid={`vaction-${s2}`} onClick={() => setStatus(s2)} className="rounded-full border border-white/10 px-3 py-1.5 text-xs text-zinc-200 hover:bg-white/5">{V_STATUS_META[s2]?.label || s2}</button>
            ))}
          </div>
        </div>

        {/* Verifica fisica */}
        {v.eligible && (
          <div className={card} data-testid="verify-block">
            <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Verifica fisica in negozio</p>
            <div className="grid gap-2 sm:grid-cols-3">
              <Field label="Esito"><select className={inp} value={verify.result} onChange={(e) => setVerify({ ...verify, result: e.target.value })}><option value="CONFORME">Conforme</option><option value="NON_CONFORME">Non conforme</option><option value="PREZZO_MODIFICATO">Prezzo modificato</option></select></Field>
              <Field label="Offerta finale (€)"><input className={inp} type="number" value={(verify.final_offer_cents ?? 0) / 100} onChange={(e) => setVerify({ ...verify, final_offer_cents: Math.round(parseFloat(e.target.value || 0) * 100) })} /></Field>
              <Field label="Motivo (se prezzo modificato)"><input className={inp} value={verify.reason} onChange={(e) => setVerify({ ...verify, reason: e.target.value })} /></Field>
            </div>
            <button data-testid="verify-submit" onClick={doVerify} className="mt-3 rounded-full bg-[#2563EB] px-4 py-2 text-sm font-semibold">Salva verifica</button>
          </div>
        )}

        {/* Acquisto */}
        {v.eligible && (
          <div className={card} data-testid="purchase-block">
            <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Registra acquisto</p>
            <div className="grid gap-2 sm:grid-cols-4">
              <Field label="Prezzo (€)"><input className={inp} type="number" value={(purchase.final_purchase_price_cents ?? 0) / 100} onChange={(e) => setPurchase({ ...purchase, final_purchase_price_cents: Math.round(parseFloat(e.target.value || 0) * 100) })} /></Field>
              <Field label="IMEI"><input className={inp} value={purchase.imei} onChange={(e) => setPurchase({ ...purchase, imei: e.target.value })} /></Field>
              <Field label="Seriale"><input className={inp} value={purchase.serial} onChange={(e) => setPurchase({ ...purchase, serial: e.target.value })} /></Field>
              <Field label="Colore"><input className={inp} value={purchase.color} onChange={(e) => setPurchase({ ...purchase, color: e.target.value })} /></Field>
            </div>
            <button data-testid="purchase-submit" onClick={doPurchase} className="mt-3 rounded-full bg-emerald-600 px-4 py-2 text-sm font-semibold">Conferma acquisto</button>
          </div>
        )}
      </div>
    </div>
  );
}
const Row = ({ k, v, bold }) => (<div className="flex justify-between"><span className="text-zinc-500">{k}</span><span className={bold ? "font-bold text-white" : ""}>{v}</span></div>);

// =========================== SIMULATORE ===========================
function Simulatore() {
  const [cfg, setCfg] = useState(null);
  const [models, setModels] = useState([]);
  const [f, setF] = useState({ model_id: "", storage: "", value_usd: "", aesthetic: "COME_NUOVO", battery_band: "B90_100", battery_original: "yes", display_original: "yes", functional_ok: true });
  const [ov, setOv] = useState({ benchmark_multiplier: "", iphix_purchase_margin_pct: "", risk_reserve_pct: "", rounding_step_eur: "" });
  const [res, setRes] = useState(null);

  useEffect(() => { vGetConfig().then(setCfg); vModels().then(setModels); }, []);
  const model = models.find((m) => m.id === f.model_id);

  const run = async () => {
    const payload = { ...f };
    if (payload.value_usd === "") delete payload.value_usd; else payload.value_usd = parseFloat(payload.value_usd);
    Object.entries(ov).forEach(([k, v]) => { if (v !== "") payload[k] = parseFloat(v); });
    try { setRes(await vSimulate(payload)); } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  if (!cfg) return null;
  const b = res?.breakdown;
  return (
    <section className="grid gap-4 lg:grid-cols-2" data-testid="valuation-simulator">
      <div className={card + " space-y-3"}>
        <p className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Parametri</p>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Modello"><select className={inp} value={f.model_id} onChange={(e) => setF({ ...f, model_id: e.target.value, storage: "" })}><option value="">— (usa USD)</option>{models.map((m) => <option key={m.id} value={m.id}>{m.model}</option>)}</select></Field>
          <Field label="Memoria"><select className={inp} value={f.storage} onChange={(e) => setF({ ...f, storage: e.target.value })}><option value="">—</option>{(model?.storages || []).map((s) => <option key={s} value={s}>{s}</option>)}</select></Field>
        </div>
        <Field label="Benchmark Apple USD (override / se nessun modello)"><input data-testid="sim-usd" className={inp} type="number" placeholder="es. 300" value={f.value_usd} onChange={(e) => setF({ ...f, value_usd: e.target.value })} /></Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Estetica"><select className={inp} value={f.aesthetic} onChange={(e) => setF({ ...f, aesthetic: e.target.value })}>{cfg.aesthetic_grades.map((g) => <option key={g.code} value={g.code}>{g.label}</option>)}</select></Field>
          <Field label="Batteria"><select className={inp} value={f.battery_band} onChange={(e) => setF({ ...f, battery_band: e.target.value })}>{cfg.battery_bands.map((x) => <option key={x.code} value={x.code}>{x.label}</option>)}</select></Field>
          <Field label="Batteria originale"><select className={inp} value={f.battery_original} onChange={(e) => setF({ ...f, battery_original: e.target.value })}><option value="yes">Sì</option><option value="no">No / Non so</option></select></Field>
          <Field label="Display originale"><select className={inp} value={f.display_original} onChange={(e) => setF({ ...f, display_original: e.target.value })}><option value="yes">Sì</option><option value="no">No / Non so</option></select></Field>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Field label={`Moltiplicatore (def ${cfg.benchmark_multiplier})`}><input className={inp} type="number" step="0.01" placeholder={cfg.benchmark_multiplier} value={ov.benchmark_multiplier} onChange={(e) => setOv({ ...ov, benchmark_multiplier: e.target.value })} /></Field>
          <Field label={`Margine % (def ${cfg.iphix_purchase_margin_pct})`}><input className={inp} type="number" placeholder={cfg.iphix_purchase_margin_pct} value={ov.iphix_purchase_margin_pct} onChange={(e) => setOv({ ...ov, iphix_purchase_margin_pct: e.target.value })} /></Field>
          <Field label={`Riserva % (def ${cfg.risk_reserve_pct})`}><input className={inp} type="number" placeholder={cfg.risk_reserve_pct} value={ov.risk_reserve_pct} onChange={(e) => setOv({ ...ov, risk_reserve_pct: e.target.value })} /></Field>
          <Field label={`Arrotond. € (def ${cfg.rounding_step_eur})`}><input className={inp} type="number" placeholder={cfg.rounding_step_eur} value={ov.rounding_step_eur} onChange={(e) => setOv({ ...ov, rounding_step_eur: e.target.value })} /></Field>
        </div>
        <button data-testid="sim-run" onClick={run} className="w-full rounded-full bg-[#2563EB] py-2.5 text-sm font-semibold">Calcola</button>
      </div>
      <div className={card}>
        <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Risultato</p>
        {!res && <p className="py-8 text-center text-sm text-zinc-500">Imposta i parametri e premi Calcola.</p>}
        {res && !res.eligibility.eligible && <p className="text-sm text-rose-400">NON ACQUISTABILE — {res.eligibility.reasons.join(", ")}</p>}
        {res && res.eligibility.eligible && b && !b.error && !b.reject && (
          <div className="space-y-1 text-sm text-zinc-300">
            <Row k="Apple Trade In USD" v={`$${b.value_usd}`} />
            <Row k="Benchmark IPHIX EUR" v={euro(b.benchmark_cents)} />
            <Row k={`Penalità capacità (${b.capacity_pct}%)`} v={`- ${euro(b.capacity_penalty_cents)}`} />
            <Row k={`Penalità estetica (${b.aesthetic_pct}%)`} v={`- ${euro(b.aesthetic_penalty_cents)}`} />
            <Row k={`Penalità batteria (${b.battery_pct}%)`} v={`- ${euro(b.battery_penalty_cents)}`} />
            <Row k="Costo batteria" v={`- ${euro(b.battery_cost_cents)}`} />
            <Row k="Batteria non originale" v={`- ${euro(b.battery_non_original_cents)}`} />
            <Row k={`Riserva rischio (${b.risk_reserve_pct}%)`} v={`- ${euro(b.risk_reserve_cents)}`} />
            <Row k="Valore pre-margine" v={euro(b.pre_margin_cents)} />
            <Row k={`Margine IPHIX (${b.margin_pct}%)`} v={`- ${euro(b.margin_cents)}`} />
            <Row k={`Arrotondamento (${b.rounding_step_eur}€)`} v={euro(b.pre_round_cents)} />
            <div className="mt-2 rounded-lg bg-[#2563EB]/15 p-3"><Row k="OFFERTA IPHIX" v={<span data-testid="sim-offer" className="text-xl">{euro(b.final_cents)}</span>} bold /></div>
          </div>
        )}
        {res && b?.reject && <p className="text-sm text-rose-400">Rifiutato: {b.reject}</p>}
        {res && b?.error === "NO_BENCHMARK" && <p className="text-sm text-amber-400">Nessun benchmark: inserisci un valore USD o un benchmark per il modello.</p>}
        {res && (res.algorithm_version || res.benchmark_version != null) && (
          <p className="mt-3 border-t border-white/10 pt-2 text-[11px] text-zinc-500" data-testid="sim-versions">
            Algorithm Version: <span className="font-semibold text-zinc-300">{res.algorithm_version || "—"}</span> · Benchmark Version: <span className="font-semibold text-zinc-300">{res.benchmark_version != null ? `v${res.benchmark_version}` : "—"}</span>
          </p>
        )}
      </div>
    </section>
  );
}

// ========================= CONFIGURAZIONE =========================
function Config() {
  const [cfg, setCfg] = useState(null);
  const [alert, setAlert] = useState(null);
  const load = () => vGetConfig().then(setCfg);
  useEffect(() => { load(); vBenchmarkAlert().then(setAlert).catch(() => {}); }, []);

  const save = async (patch, reason) => {
    try { setCfg(await vPutConfig({ ...patch, _reason: reason })); toast.success("Salvato"); } catch { toast.error("Errore"); }
  };
  if (!cfg) return null;

  const num = (label, key, step = "1") => (
    <Field label={label}><input className={inp} type="number" step={step} defaultValue={cfg[key]} onBlur={(e) => { const v = parseFloat(e.target.value); if (!isNaN(v) && v !== cfg[key]) save({ [key]: v }); }} /></Field>
  );

  return (
    <section className="space-y-4" data-testid="valuation-config">
      {alert && alert.stale_count > 0 && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-300" data-testid="benchmark-alert">
          ⚠️ Benchmark Apple da verificare: {alert.stale_count} valori più vecchi di 30 giorni. L'aggiornamento è manuale.
        </div>
      )}

      <div className={card + " space-y-3"}>
        <p className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Parametri generali</p>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {num("Moltiplicatore Benchmark Apple", "benchmark_multiplier", "0.01")}
          {num("Margine IPHIX (%)", "iphix_purchase_margin_pct")}
          {num("Riserva rischio (%)", "risk_reserve_pct")}
          {num("Arrotondamento (€)", "rounding_step_eur")}
          {num("Validità preventivo (giorni)", "validity_days")}
          <Field label="Versione algoritmo"><input className={inp} defaultValue={cfg.algorithm_version} onBlur={(e) => e.target.value !== cfg.algorithm_version && save({ algorithm_version: e.target.value })} /></Field>
        </div>
      </div>

      <div className={card}>
        <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-zinc-500">Penalità estetiche (%)</p>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {cfg.aesthetic_grades.map((g, i) => (
            <Field key={g.code} label={g.label}><input className={inp} type="number" defaultValue={g.penalty_pct} onBlur={(e) => { const v = parseFloat(e.target.value); if (!isNaN(v)) { const arr = [...cfg.aesthetic_grades]; arr[i] = { ...g, penalty_pct: v }; save({ aesthetic_grades: arr }); } }} /></Field>
          ))}
        </div>
      </div>

      <div className={card}>
        <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-zinc-500">Penalità batteria (%)</p>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {cfg.battery_bands.map((x, i) => (
            <Field key={x.code} label={x.label}><input className={inp} type="number" defaultValue={x.penalty_pct} onBlur={(e) => { const v = parseFloat(e.target.value); if (!isNaN(v)) { const arr = [...cfg.battery_bands]; arr[i] = { ...x, penalty_pct: v }; save({ battery_bands: arr }); } }} /></Field>
          ))}
        </div>
        <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
          <Field label="Modalità batteria"><select className={inp} defaultValue={cfg.battery_adjustment_mode} onChange={(e) => save({ battery_adjustment_mode: e.target.value })}><option value="PERCENTAGE">Percentuale</option><option value="FIXED_AMOUNT">Importo fisso</option><option value="REPLACEMENT_COST">Costo sostituzione</option><option value="REJECT">Rifiuta</option></select></Field>
          <Field label="Batteria non orig. — modalità"><select className={inp} defaultValue={cfg.battery_non_original_mode} onChange={(e) => save({ battery_non_original_mode: e.target.value })}><option value="PERCENTAGE">Percentuale</option><option value="FIXED_AMOUNT">Importo fisso</option><option value="REPLACEMENT_COST">Costo sostituzione</option><option value="REJECT">Rifiuta</option></select></Field>
          {num("Batteria non orig. (%)", "battery_non_original_penalty_pct")}
        </div>
      </div>

      <div className={card}>
        <p className="mb-1 text-xs font-semibold uppercase tracking-widest text-zinc-500">Penalità capacità (%) — globale</p>
        <p className="mb-3 text-[11px] text-zinc-500">La capacità massima del modello è il riferimento (0%). Le memorie inferiori applicano la penalità. Il benchmark Apple è un MODEL_MAX_UP_TO per modello.</p>
        <div className="grid grid-cols-3 gap-3 sm:grid-cols-5">
          {(cfg.capacity_penalties?.global || []).map((r, i) => (
            <Field key={r.storage} label={r.storage}><input data-testid={`cap-${r.storage.replace(/\s/g, "")}`} className={inp} type="number" defaultValue={r.penalty_pct} onBlur={(e) => { const v = parseFloat(e.target.value); if (!isNaN(v)) { const arr = [...cfg.capacity_penalties.global]; arr[i] = { ...r, penalty_pct: v }; save({ capacity_penalties: { ...cfg.capacity_penalties, global: arr } }); } }} /></Field>
          ))}
        </div>
      </div>

      <Models />
      <Benchmarks onSaved={() => vBenchmarkAlert().then(setAlert)} />
    </section>
  );
}

function Models() {
  const [rows, setRows] = useState([]);
  const [nm, setNm] = useState({ model: "", storages: "", battery_eur: "" });
  const load = () => vModels().then(setRows);
  useEffect(() => { load(); }, []);
  const add = async () => { if (!nm.model) return; try { await vCreateModel({ model: nm.model, storages: nm.storages.split(",").map((s) => s.trim()).filter(Boolean), battery_replacement_cost_cents: nm.battery_eur === "" ? null : Math.round(parseFloat(nm.battery_eur) * 100) }); setNm({ model: "", storages: "", battery_eur: "" }); load(); toast.success("Modello aggiunto"); } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); } };
  const saveBattery = async (m, eur) => { const cents = eur === "" ? null : Math.round(parseFloat(eur) * 100); if (cents === (m.battery_replacement_cost_cents ?? null)) return; try { await vPatchModel(m.id, { battery_replacement_cost_cents: cents }); load(); toast.success("Costo batteria salvato"); } catch { toast.error("Errore"); } };
  return (
    <div className={card} data-testid="valuation-models">
      <p className="mb-1 text-xs font-semibold uppercase tracking-widest text-zinc-500">Modelli supportati ({rows.length})</p>
      <p className="mb-3 text-[11px] text-zinc-500">Il costo batteria per modello viene sottratto quando la modalità batteria (o batteria non originale) è impostata su "Costo sostituzione".</p>
      <div className="max-h-72 space-y-1 overflow-y-auto">
        {rows.map((m) => (
          <div key={m.id} className="flex items-center gap-2 rounded-lg border border-white/5 px-3 py-1.5 text-sm">
            <span className="min-w-[110px] font-medium">{m.model}</span>
            <span className="flex-1 truncate text-xs text-zinc-500">{(m.storages || []).join(" · ")}</span>
            <span className="text-[10px] text-zinc-500">Batt. €</span>
            <input data-testid={`model-batt-${m.id}`} className="w-20 rounded-md border border-white/10 bg-[#0B0E13] px-2 py-1 text-xs text-white outline-none focus:border-[#2563EB]" type="number" defaultValue={m.battery_replacement_cost_cents != null ? (m.battery_replacement_cost_cents / 100) : ""} onBlur={(e) => saveBattery(m, e.target.value)} />
            <span className="text-[10px] text-zinc-500">Molt.×</span>
            <input data-testid={`model-mult-${m.id}`} className="w-16 rounded-md border border-white/10 bg-[#0B0E13] px-2 py-1 text-xs text-white outline-none focus:border-[#2563EB]" type="number" step="0.01" placeholder="glob." defaultValue={m.benchmark_multiplier_override ?? ""} onBlur={(e) => { const v = e.target.value === "" ? null : parseFloat(e.target.value); if (v !== (m.benchmark_multiplier_override ?? null)) vPatchModel(m.id, { benchmark_multiplier_override: v }).then(load).then(() => toast.success("Moltiplicatore modello salvato")); }} />
            <button onClick={() => vPatchModel(m.id, { active: !m.active }).then(load)} className={`rounded-full px-2 py-0.5 text-xs ${m.active ? "bg-emerald-500/15 text-emerald-400" : "bg-zinc-500/15 text-zinc-400"}`}>{m.active ? "Attivo" : "Inattivo"}</button>
            <button onClick={() => vDeleteModel(m.id).then(load)} className="rounded-full border border-white/10 px-2 py-0.5 text-xs text-rose-400">Elimina</button>
          </div>
        ))}
      </div>
      <div className="mt-3 grid grid-cols-3 gap-2">
        <input className={inp} placeholder="Nome modello (es. iPhone 18)" value={nm.model} onChange={(e) => setNm({ ...nm, model: e.target.value })} />
        <input className={inp} placeholder="Memorie: 128 GB, 256 GB" value={nm.storages} onChange={(e) => setNm({ ...nm, storages: e.target.value })} />
        <input className={inp} type="number" placeholder="Costo batteria €" value={nm.battery_eur} onChange={(e) => setNm({ ...nm, battery_eur: e.target.value })} />
      </div>
      <button data-testid="model-add" onClick={add} className="mt-2 rounded-full bg-[#2563EB] px-4 py-2 text-sm font-semibold">Aggiungi modello</button>
    </div>
  );
}

function Benchmarks({ onSaved }) {
  const [models, setModels] = useState([]);
  const [mid, setMid] = useState("");
  const [storage, setStorage] = useState("");
  const [active, setActive] = useState([]);
  const [nv, setNv] = useState({ value_usd: "", source_reference: "" });
  useEffect(() => { vModels().then(setModels); }, []);
  const model = models.find((m) => m.id === mid);
  const loadActive = () => vBenchmarks({}).then(setActive);
  useEffect(() => { loadActive(); }, []);

  const add = async () => {
    if (!mid || !storage || nv.value_usd === "") { toast.error("Compila modello, memoria e valore USD"); return; }
    try { await vAddBenchmark({ model_id: mid, storage, value_usd: parseFloat(nv.value_usd), source_reference: nv.source_reference }); setNv({ value_usd: "", source_reference: "" }); loadActive(); onSaved?.(); toast.success("Benchmark salvato (nuova versione)"); } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  return (
    <div className={card} data-testid="valuation-benchmarks">
      <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-zinc-500">Benchmark Apple Trade-In USA</p>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Field label="Modello"><select className={inp} value={mid} onChange={(e) => { setMid(e.target.value); setStorage(""); }}><option value="">—</option>{models.map((m) => <option key={m.id} value={m.id}>{m.model}</option>)}</select></Field>
        <Field label="Memoria"><select className={inp} value={storage} onChange={(e) => setStorage(e.target.value)}><option value="">—</option>{(model?.storages || []).map((s) => <option key={s} value={s}>{s}</option>)}</select></Field>
        <Field label="Valore USD"><input data-testid="bench-usd" className={inp} type="number" value={nv.value_usd} onChange={(e) => setNv({ ...nv, value_usd: e.target.value })} /></Field>
        <Field label="Riferimento fonte"><input className={inp} placeholder="URL/nota Apple" value={nv.source_reference} onChange={(e) => setNv({ ...nv, source_reference: e.target.value })} /></Field>
      </div>
      <button data-testid="bench-add" onClick={add} className="mt-2 rounded-full bg-[#2563EB] px-4 py-2 text-sm font-semibold">Aggiungi versione benchmark</button>
      <div className="mt-4 max-h-56 overflow-y-auto">
        <table className="w-full text-left text-xs">
          <thead className="text-zinc-500"><tr><th className="p-1">Modello</th><th className="p-1">Mem.</th><th className="p-1">USD</th><th className="p-1">EUR</th><th className="p-1">v</th><th className="p-1">Data</th></tr></thead>
          <tbody>{active.map((r) => (<tr key={r.id} className="border-t border-white/5"><td className="p-1">{r.model}</td><td className="p-1">{r.storage}</td><td className="p-1">${r.value_usd}</td><td className="p-1">€{r.value_eur}</td><td className="p-1">v{r.version}</td><td className="p-1 text-zinc-500">{new Date(r.observed_at).toLocaleDateString("it-IT")}</td></tr>))}</tbody>
        </table>
        {active.length === 0 && <p className="py-4 text-center text-xs text-zinc-500">Nessun benchmark attivo. Inseriscili qui.</p>}
      </div>
    </div>
  );
}

// ===================== ULTIMO CONTROLLO BENCHMARK (card 2C) =====================
function BenchmarkCheckCard() {
  const [state, setState] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => { vBenchmarkCheckState().then(setState).catch(() => {}); }, []);
  const s = state?.last_run;
  const counts = s?.counts || {};
  const chips = [
    ["Verificati", counts.VERIFIED ?? 0, "bg-emerald-500/15 text-emerald-400"],
    ["UNCHANGED", counts.UNCHANGED ?? 0, "bg-zinc-500/15 text-zinc-300"],
    ["CHANGED", counts.CHANGED ?? 0, "bg-blue-500/15 text-blue-400"],
    ["NOT_VERIFIED", counts.NOT_VERIFIED ?? 0, "bg-amber-500/15 text-amber-400"],
    ["CHECK_FAILED", counts.CHECK_FAILED ?? 0, "bg-rose-500/15 text-rose-400"],
    ["Proposte PENDING", state?.pending_proposals ?? 0, "bg-indigo-500/15 text-indigo-400"],
  ];
  const results = s?.results || [];
  return (
    <div className={card + " space-y-3"} data-testid="benchmark-check-card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-semibold">📊 Ultimo controllo benchmark</p>
        {s?.last_run && <button data-testid="benchmark-check-details-btn" onClick={() => setOpen((o) => !o)} className="rounded-full border border-white/10 px-3 py-1 text-xs text-zinc-300">{open ? "Nascondi dettagli" : "Vedi dettagli"}</button>}
      </div>
      {!s?.last_run ? (
        <p className="text-sm text-zinc-500" data-testid="benchmark-check-never">Ultimo controllo: non ancora eseguito</p>
      ) : (
        <>
          <p className="text-xs text-zinc-400" data-testid="benchmark-check-time">Ultimo controllo automatico Apple USA: <span className="font-semibold text-zinc-200">{new Date(s.last_run).toLocaleString("it-IT")}</span></p>
          <div className="flex flex-wrap gap-2">
            {chips.map(([label, val, cls]) => (
              <span key={label} className={`rounded-full px-3 py-1 text-xs ${cls}`} data-testid={`bcheck-${label}`}>{label}: <span className="font-bold">{val}</span></span>
            ))}
          </div>
          {open && (
            <div className="max-h-72 overflow-y-auto rounded-lg border border-white/5" data-testid="benchmark-check-details">
              <table className="w-full text-left text-xs">
                <thead className="text-zinc-500"><tr><th className="p-2">Modello</th><th className="p-2">Precedente</th><th className="p-2">Recuperato</th><th className="p-2">Stato</th></tr></thead>
                <tbody>
                  {results.map((r) => (
                    <tr key={r.model_id} className="border-t border-white/5">
                      <td className="p-2">{r.model}</td>
                      <td className="p-2 text-zinc-400">{r.old_value_usd != null ? `$${r.old_value_usd}` : "—"}</td>
                      <td className="p-2 text-zinc-400">{r.found_value_usd != null ? `$${r.found_value_usd}` : "—"}</td>
                      <td className="p-2">{r.status}</td>
                    </tr>
                  ))}
                  {results.length === 0 && <tr><td colSpan={4} className="p-3 text-center text-zinc-500">Nessun dettaglio disponibile.</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Aggiornamenti() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => vProposals().then(setRows).catch(() => {});
  useEffect(() => { load(); }, []);
  const check = async () => { setBusy(true); try { const r = await vBenchmarkCheck(); toast.success(`Verifica: ${r.pending_created} proposte, ${r.verification_failed} non verificate, ${r.unchanged} invariate`); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Errore verifica"); } setBusy(false); };
  const approve = async (id) => { try { await vApproveProposal(id); toast.success("Approvata: benchmark ACTIVE aggiornato"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); } };
  const reject = async (id) => { const reason = window.prompt("Motivo del rifiuto (opzionale):") || ""; try { await vRejectProposal(id, reason); toast.success("Rifiutata: benchmark invariato"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); } };
  const pending = rows.filter((r) => r.status === "PENDING_APPROVAL");
  const others = rows.filter((r) => r.status !== "PENDING_APPROVAL");
  const badge = { PENDING_APPROVAL: "bg-amber-500/15 text-amber-400", APPROVED: "bg-emerald-500/15 text-emerald-400", REJECTED: "bg-rose-500/15 text-rose-400", VERIFICATION_FAILED: "bg-zinc-500/15 text-zinc-400" };
  return (
    <section className="space-y-4" data-testid="valuation-updates">
      <BenchmarkCheckCard />
      <div className={card + " flex items-center justify-between"}>
        <div><p className="text-sm font-semibold">Aggiornamenti benchmark Apple</p><p className="text-[11px] text-zinc-500">Il controllo (manuale o automatico ogni 30gg) crea proposte. I benchmark ACTIVE NON cambiano finché non approvi.</p></div>
        <button data-testid="benchmark-check-btn" onClick={check} disabled={busy} className="rounded-full bg-[#2563EB] px-4 py-2 text-sm font-semibold disabled:opacity-50">{busy ? "Verifica…" : "Verifica benchmark ora"}</button>
      </div>
      <div className={card}>
        <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">In attesa di approvazione ({pending.length})</p>
        {pending.length === 0 && <p className="py-4 text-center text-xs text-zinc-500">Nessuna proposta in attesa.</p>}
        {pending.map((r) => (
          <div key={r.id} data-testid={`proposal-${r.id}`} className="mb-2 flex flex-wrap items-center gap-3 rounded-lg border border-white/5 p-3 text-sm">
            <span className="min-w-[140px] font-medium">{r.model}</span>
            <span className="text-zinc-400">${r.old_value_usd ?? "—"} → <span className="text-white font-semibold">${r.new_value_usd ?? "—"}</span></span>
            {r.change_usd != null && <span className={r.change_usd < 0 ? "text-rose-400" : "text-emerald-400"}>{r.change_usd > 0 ? "+" : ""}{r.change_usd}$ ({r.change_percent}%)</span>}
            <span className="text-[10px] text-zinc-500">{r.source}</span>
            <div className="ml-auto flex gap-2">
              <button data-testid={`approve-${r.id}`} onClick={() => approve(r.id)} className="rounded-full bg-emerald-500/20 px-3 py-1 text-xs text-emerald-300">APPROVA</button>
              <button data-testid={`reject-${r.id}`} onClick={() => reject(r.id)} className="rounded-full bg-rose-500/20 px-3 py-1 text-xs text-rose-300">RIFIUTA</button>
            </div>
          </div>
        ))}
      </div>
      <div className={card}>
        <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Storico proposte / decisioni ({others.length})</p>
        <div className="max-h-72 overflow-y-auto">
          {others.map((r) => (
            <div key={r.id} className="flex flex-wrap items-center gap-2 border-t border-white/5 py-1.5 text-xs">
              <span className={`rounded-full px-2 py-0.5 ${badge[r.status]}`}>{r.status}</span>
              <span className="font-medium">{r.model}</span>
              <span className="text-zinc-400">${r.old_value_usd ?? "—"} → ${r.new_value_usd ?? "—"}</span>
              {r.error && <span className="text-zinc-500">{r.error}</span>}
              {r.reject_reason && <span className="text-zinc-500">motivo: {r.reject_reason}</span>}
              <span className="ml-auto text-zinc-500">{r.decided_by || r.requested_by} · {new Date(r.retrieved_at || r.created_at).toLocaleDateString("it-IT")}</span>
            </div>
          ))}
          {others.length === 0 && <p className="py-4 text-center text-xs text-zinc-500">Nessuno storico.</p>}
        </div>
      </div>
    </section>
  );
}

// ===================== CURVA SVALUTAZIONE (2D, analitica) =====================
const PERIODS = [["30", "30 giorni"], ["90", "90 giorni"], ["180", "180 giorni"]];

function StatChart({ points, field, color }) {
  const pts = (points || []).map((p) => ({ t: new Date(p.observed_at).getTime(), v: p[field] })).filter((p) => p.v != null);
  const W = 640, H = 220, P = 36;
  if (pts.length === 0) return <p className="py-10 text-center text-sm text-zinc-500">Nessun dato storico disponibile.</p>;
  const vmin = Math.min(...pts.map((p) => p.v)), vmax = Math.max(...pts.map((p) => p.v));
  const span = (vmax - vmin) || Math.max(1, vmax * 0.1);
  const pad = span * 0.15;
  const lo = vmin - pad, hi = vmax + pad;
  const x = (i) => (pts.length > 1 ? P + (i * (W - 2 * P)) / (pts.length - 1) : W / 2);
  const y = (v) => H - P - ((v - lo) / (hi - lo)) * (H - 2 * P);
  const path = pts.map((p, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${y(p.v).toFixed(1)}`).join(" ");
  const area = `${path} L${x(pts.length - 1).toFixed(1)},${(H - P).toFixed(1)} L${x(0).toFixed(1)},${(H - P).toFixed(1)} Z`;
  const fdate = (t) => new Date(t).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit" });
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="h-auto w-full" data-testid="curve-svg" preserveAspectRatio="xMidYMid meet">
      <defs>
        <linearGradient id="curveFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.28" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      {[0, 0.5, 1].map((g) => (
        <line key={g} x1={P} x2={W - P} y1={P + g * (H - 2 * P)} y2={P + g * (H - 2 * P)} stroke="#ffffff" strokeOpacity="0.06" />
      ))}
      <text x={P} y={P - 10} fill="#71717a" fontSize="11">{field === "value_usd" ? `$${vmax}` : `€${vmax}`}</text>
      <text x={P} y={H - P + 16} fill="#71717a" fontSize="11">{field === "value_usd" ? `$${vmin}` : `€${vmin}`}</text>
      {pts.length > 1 && <path d={area} fill="url(#curveFill)" />}
      <path d={path} fill="none" stroke={color} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
      {pts.map((p, i) => (
        <circle key={i} cx={x(i)} cy={y(p.v)} r={pts.length > 1 ? 3 : 5} fill={color} />
      ))}
      <text x={P} y={H - 6} fill="#71717a" fontSize="10">{fdate(pts[0].t)}</text>
      {pts.length > 1 && <text x={W - P} y={H - 6} textAnchor="end" fill="#71717a" fontSize="10">{fdate(pts[pts.length - 1].t)}</text>}
    </svg>
  );
}

function DeltaStat({ label, stat, cur }) {
  const sym = cur === "USD" ? "$" : "€";
  if (!stat || !stat.sufficient) {
    return (
      <div className="rounded-lg border border-white/5 bg-[#0B0E13] p-3" data-testid={`stat-${label}`}>
        <p className="text-[11px] uppercase tracking-wide text-zinc-500">{label}</p>
        <p className="mt-1 text-sm text-zinc-500">N/D — dati insufficienti</p>
      </div>
    );
  }
  const down = stat.diff_abs < 0;
  const cls = stat.diff_abs === 0 ? "text-zinc-300" : down ? "text-rose-400" : "text-emerald-400";
  return (
    <div className="rounded-lg border border-white/5 bg-[#0B0E13] p-3" data-testid={`stat-${label}`}>
      <p className="text-[11px] uppercase tracking-wide text-zinc-500">{label}</p>
      <p className="mt-1 text-xs text-zinc-400">Iniziale <span className="font-semibold text-zinc-200">{sym}{stat.initial}</span> → Attuale <span className="font-semibold text-zinc-200">{sym}{stat.current}</span></p>
      <p className={`mt-1 text-sm font-semibold ${cls}`}>{stat.diff_abs > 0 ? "+" : ""}{sym}{stat.diff_abs} {stat.diff_pct != null && <span className="text-xs">({stat.diff_pct > 0 ? "+" : ""}{stat.diff_pct}%)</span>}</p>
      {stat.cumulative_loss > 0 && <p className="text-[11px] text-zinc-500">Perdita: {sym}{stat.cumulative_loss}</p>}
    </div>
  );
}

function CurvaSvalutazione() {
  const [models, setModels] = useState([]);
  const [mid, setMid] = useState("");
  const [data, setData] = useState(null);
  const [cur, setCur] = useState("EUR");
  const [period, setPeriod] = useState("30");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    vBenchmarks({}).then((bs) => {
      const ids = new Set((bs || []).map((b) => b.model_id));
      vModels().then((ms) => {
        const filtered = (ms || []).filter((m) => ids.has(m.id));
        setModels(filtered);
        if (filtered[0] && !mid) setMid(filtered[0].id);
      });
    }).catch(() => {});
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!mid) return;
    setLoading(true);
    vBenchmarkHistory(mid).then(setData).catch(() => setData(null)).finally(() => setLoading(false));
  }, [mid]);

  const field = cur === "USD" ? "value_usd" : "value_eur";
  const sym = cur === "USD" ? "$" : "€";
  const color = cur === "USD" ? "#22c55e" : "#2563EB";
  const stats = data?.stats?.[cur] || {};
  const curVal = data?.current ? (cur === "USD" ? data.current.value_usd : data.current.value_eur) : null;

  return (
    <section className="space-y-4" data-testid="valuation-curve">
      <div className={card + " space-y-1"}>
        <p className="text-lg font-bold">📉 Curva Svalutazione iPhone</p>
        <p className="text-[11px] text-zinc-500">Sezione esclusivamente analitica. Non modifica benchmark, offerte, algoritmo, moltiplicatori, penalità, margini o valutazioni già emesse. Basata solo sullo storico versionato reale.</p>
      </div>

      <div className={card}>
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[200px] flex-1">
            <Field label="Modello iPhone">
              <select data-testid="curve-model" className={inp} value={mid} onChange={(e) => setMid(e.target.value)}>
                {models.length === 0 && <option value="">Nessun modello con benchmark</option>}
                {models.map((m) => <option key={m.id} value={m.id}>{m.model}</option>)}
              </select>
            </Field>
          </div>
          <div className="flex overflow-hidden rounded-full border border-white/10" data-testid="curve-currency">
            {["EUR", "USD"].map((c) => (
              <button key={c} data-testid={`curve-cur-${c}`} onClick={() => setCur(c)} className={`px-4 py-2 text-xs font-semibold ${cur === c ? "bg-[#2563EB] text-white" : "text-zinc-300"}`}>
                {c === "EUR" ? "Benchmark IPHIX EUR" : "Apple Max USD"}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading && <div className={card}><p className="py-8 text-center text-sm text-zinc-500">Caricamento storico…</p></div>}

      {!loading && data && (
        <>
          <div className={card + " space-y-3"}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Andamento storico — {cur === "USD" ? "Apple Max (USD)" : "Benchmark IPHIX (EUR)"}</p>
              <p className="text-sm">Benchmark attuale: <span data-testid="curve-current" className="font-bold text-white">{curVal != null ? `${sym}${curVal}` : "—"}</span></p>
            </div>
            <StatChart points={data.points} field={field} color={color} />
            {!data.has_history && (
              <p className="rounded-lg border border-amber-500/25 bg-amber-500/10 p-2 text-center text-xs text-amber-300" data-testid="curve-insufficient">
                Non ci sono ancora dati sufficienti: è disponibile solo il benchmark corrente. Lo storico si popolerà con i prossimi aggiornamenti verificati.
              </p>
            )}
          </div>

          <div className={card + " space-y-3"}>
            <p className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Statistiche periodo</p>
            <div className="grid gap-3 sm:grid-cols-3">
              <DeltaStat label="30gg" stat={stats["30"]} cur={cur} />
              <DeltaStat label="90gg" stat={stats["90"]} cur={cur} />
              <DeltaStat label="180gg" stat={stats["180"]} cur={cur} />
            </div>
            <div className="rounded-lg border border-white/5 bg-[#0B0E13] p-3" data-testid="stat-cumulative">
              <p className="text-[11px] uppercase tracking-wide text-zinc-500">Perdita cumulativa (dall'inizio dello storico)</p>
              {stats.cumulative?.sufficient ? (
                <p className={`mt-1 text-sm font-semibold ${stats.cumulative.diff_abs < 0 ? "text-rose-400" : stats.cumulative.diff_abs > 0 ? "text-emerald-400" : "text-zinc-300"}`}>
                  {sym}{stats.cumulative.initial} → {sym}{stats.cumulative.current} · {stats.cumulative.diff_abs > 0 ? "+" : ""}{sym}{stats.cumulative.diff_abs} ({stats.cumulative.diff_pct}%)
                  {stats.cumulative.cumulative_loss > 0 && <span className="ml-2 text-xs text-zinc-400">perdita {sym}{stats.cumulative.cumulative_loss}</span>}
                </p>
              ) : <p className="mt-1 text-sm text-zinc-500">N/D — dati insufficienti</p>}
            </div>
          </div>

          <div className={card + " space-y-3"}>
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Riepilogo periodo</p>
              <div className="ml-auto flex gap-1">
                {PERIODS.map(([id, label]) => (
                  <button key={id} data-testid={`curve-period-${id}`} onClick={() => setPeriod(id)} className={`rounded-full px-3 py-1 text-xs ${period === id ? "bg-white text-[#0B0E13]" : "border border-white/10 text-zinc-300"}`}>{label}</button>
                ))}
              </div>
            </div>
            {(() => {
              const st = stats[period];
              if (!st || !st.sufficient) return <p className="py-4 text-center text-sm text-zinc-500" data-testid="curve-summary">N/D — dati insufficienti per {period} giorni.</p>;
              return (
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-5" data-testid="curve-summary">
                  <div><p className="text-[11px] text-zinc-500">Valore iniziale</p><p className="font-semibold">{sym}{st.initial}</p></div>
                  <div><p className="text-[11px] text-zinc-500">Valore attuale</p><p className="font-semibold">{sym}{st.current}</p></div>
                  <div><p className="text-[11px] text-zinc-500">Δ {sym}</p><p className={`font-semibold ${st.diff_abs < 0 ? "text-rose-400" : st.diff_abs > 0 ? "text-emerald-400" : ""}`}>{st.diff_abs > 0 ? "+" : ""}{sym}{st.diff_abs}</p></div>
                  <div><p className="text-[11px] text-zinc-500">Δ %</p><p className={`font-semibold ${st.diff_abs < 0 ? "text-rose-400" : st.diff_abs > 0 ? "text-emerald-400" : ""}`}>{st.diff_pct != null ? `${st.diff_pct > 0 ? "+" : ""}${st.diff_pct}%` : "—"}</p></div>
                  <div><p className="text-[11px] text-zinc-500">Perdita cumulativa</p><p className="font-semibold text-zinc-200">{st.cumulative_loss > 0 ? `${sym}${st.cumulative_loss}` : "—"}</p></div>
                </div>
              );
            })()}
          </div>
        </>
      )}
    </section>
  );
}

// ===================== VERSIONI ALGORITMO (2E) =====================
const ALGO_STATUS_CLS = { DRAFT: "bg-amber-500/15 text-amber-400", ACTIVE: "bg-emerald-500/15 text-emerald-400", ARCHIVED: "bg-zinc-500/15 text-zinc-400" };
const DRAFT_FIELDS = [
  ["benchmark_multiplier", "Moltiplicatore benchmark", "0.01"],
  ["iphix_purchase_margin_pct", "Margine IPHIX (%)", "1"],
  ["risk_reserve_pct", "Riserva rischio (%)", "1"],
  ["rounding_step_eur", "Arrotondamento (€)", "1"],
  ["validity_days", "Validità (giorni)", "1"],
  ["battery_non_original_penalty_pct", "Batteria non orig. (%)", "1"],
];
const fmtParam = (v) => (v == null ? "—" : (typeof v === "object" ? JSON.stringify(v) : String(v)));
const PARAM_LABELS = {
  benchmark_multiplier: "Moltiplicatore benchmark", iphix_purchase_margin_pct: "Margine IPHIX (%)",
  risk_reserve_pct: "Riserva rischio (%)", rounding_step_eur: "Arrotondamento (€)",
  validity_days: "Validità (giorni)", battery_non_original_penalty_pct: "Batteria non orig. (%)",
  battery_fixed_amount_cents: "Batteria importo fisso (cent)", battery_replacement_cost_cents: "Costo sostituzione batteria (cent)",
  battery_non_original_fixed_cents: "Batteria non orig. fisso (cent)", battery_adjustment_mode: "Modalità batteria",
  battery_non_original_mode: "Modalità batteria non orig.", pricing_method: "Metodo di prezzo",
  aesthetic_grades: "Penalità estetiche", battery_bands: "Penalità batteria (fasce)", capacity_penalties: "Penalità capacità",
};
const PP_PARAMS = new Set(["iphix_purchase_margin_pct", "risk_reserve_pct", "battery_non_original_penalty_pct"]);
const diffArrow = (d) => (d === "UP" ? "↑" : d === "DOWN" ? "↓" : "=");
const diffBadgeCls = (d) => `rounded-full px-2 py-0.5 ${d === "UP" ? "bg-emerald-500/15 text-emerald-400" : d === "DOWN" ? "bg-rose-500/15 text-rose-400" : "bg-zinc-500/15 text-zinc-300"}`;

function DiffTable({ res, testid = "algo-cmp-table" }) {
  if (!res) return null;
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-3 text-[11px] text-zinc-500" data-testid="algo-cmp-legend">
        <span className="text-emerald-400">↑ AUMENTATO</span>
        <span className="text-rose-400">↓ DIMINUITO</span>
        <span className="text-zinc-300">= INVARIATO</span>
        <span className="text-amber-400">MODIFICATO (non numerico)</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm" data-testid={testid}>
          <thead className="text-xs text-zinc-500"><tr><th className="p-2">Parametro</th><th className="p-2">{res.a.name}</th><th className="p-2">{res.b.name}</th><th className="p-2">Δ</th><th className="p-2">Δ %</th></tr></thead>
          <tbody>
            {res.diffs.map((d) => (
              <tr key={d.param} className={`border-t border-white/5 ${d.changed ? "bg-amber-500/5" : ""}`}>
                <td className="p-2 text-zinc-400">{PARAM_LABELS[d.param] || d.param}</td>
                <td className="p-2 text-xs max-w-[150px] truncate">{fmtParam(d.value_a)}</td>
                <td className="p-2 text-xs max-w-[150px] truncate">{fmtParam(d.value_b)}</td>
                <td className="p-2 text-xs whitespace-nowrap">
                  {d.numeric
                    ? <span className={diffBadgeCls(d.direction)}>{diffArrow(d.direction)} {d.delta > 0 ? "+" : ""}{d.delta}{PP_PARAMS.has(d.param) ? " pt" : ""}</span>
                    : <span className={d.changed ? "text-amber-400" : "text-zinc-500"}>{d.changed ? "MODIFICATO" : "INVARIATO"}</span>}
                </td>
                <td className="p-2 text-xs whitespace-nowrap">{d.numeric && d.delta_pct != null ? `${d.delta_pct > 0 ? "+" : ""}${d.delta_pct}%` : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function VersioniAlgoritmo() {
  const [rows, setRows] = useState([]);
  const [view, setView] = useState(null);
  const [draft, setDraft] = useState(null);
  const [creating, setCreating] = useState(false);
  const [nv, setNv] = useState({ name: "", description: "" });
  const [cmp, setCmp] = useState({ a: "", b: "", res: null });
  const [activate, setActivate] = useState(null);

  const load = () => vAlgoVersions().then(setRows).catch(() => {});
  useEffect(() => { load(); }, []);
  const active = rows.find((r) => r.status === "ACTIVE");

  const doCreate = async () => {
    if (!nv.name.trim()) return toast.error("Inserisci un nome versione");
    try { await vAlgoCreate({ name: nv.name.trim(), description: nv.description }); toast.success("Bozza creata"); setCreating(false); setNv({ name: "", description: "" }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const askActivate = async (r) => {
    if (!active) return toast.error("Nessuna versione ACTIVE di riferimento");
    try { const diff = await vAlgoCompare(active.id, r.id); setActivate({ target: r, diff }); }
    catch { toast.error("Errore nel calcolo delle differenze"); }
  };
  const confirmActivate = async () => {
    try { await vAlgoActivate(activate.target.id); toast.success(`Versione ${activate.target.name} attivata`); setActivate(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const doArchive = async (r) => { if (!window.confirm(`Archiviare la versione ${r.name}?`)) return; try { await vAlgoArchive(r.id); toast.success("Archiviata"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); } };
  const runCompare = async () => { if (!cmp.a || !cmp.b) return toast.error("Seleziona due versioni"); try { setCmp({ ...cmp, res: await vAlgoCompare(cmp.a, cmp.b) }); } catch { toast.error("Errore confronto"); } };
  const saveDraft = async () => {
    const params = {}; DRAFT_FIELDS.forEach(([k]) => { params[k] = draft.params?.[k]; });
    try { await vAlgoPatch(draft.id, { description: draft.description, params }); toast.success("Bozza salvata"); setDraft(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  return (
    <section className="space-y-4" data-testid="valuation-versions">
      <div className={card + " space-y-1"}>
        <p className="text-lg font-bold">📐 Versioni Algoritmo</p>
        <p className="text-[11px] text-zinc-500">Tracciabilità e audit dei parametri della Valuation Engine. Solo la versione ACTIVE è usata dal motore. Le vecchie valutazioni restano collegate alla versione con cui sono state emesse (snapshot immutabile).</p>
      </div>

      {active && (
        <div className={card + " space-y-2"} data-testid="algo-active-card">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`rounded-full px-2 py-0.5 text-xs ${ALGO_STATUS_CLS.ACTIVE}`}>ACTIVE</span>
            <p className="text-base font-bold" data-testid="algo-active-name">{active.name}</p>
            <p className="text-xs text-zinc-500">attivata {active.activated_at ? new Date(active.activated_at).toLocaleDateString("it-IT") : "—"}</p>
          </div>
          {active.description && <p className="text-xs text-zinc-400">{active.description}</p>}
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {DRAFT_FIELDS.map(([k, label]) => <div key={k}><p className="text-[11px] text-zinc-500">{label}</p><p className="text-sm font-semibold">{fmtParam(active.params?.[k])}</p></div>)}
          </div>
        </div>
      )}

      <button data-testid="algo-create-btn" onClick={() => setCreating(true)} className="rounded-full bg-[#2563EB] px-4 py-2 text-sm font-semibold">+ Crea nuova versione (da ACTIVE)</button>

      <div className={card + " overflow-x-auto"}>
        <table className="w-full text-left text-sm" data-testid="algo-table">
          <thead className="text-xs text-zinc-500"><tr><th className="p-2">Versione</th><th className="p-2">Stato</th><th className="p-2">Creazione</th><th className="p-2">Attivazione</th><th className="p-2">Descrizione</th><th className="p-2">Creato da</th><th className="p-2">Azioni</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} data-testid={`algo-row-${r.name}`} className="border-t border-white/5">
                <td className="p-2 font-semibold">{r.name}</td>
                <td className="p-2"><span className={`rounded-full px-2 py-0.5 text-xs ${ALGO_STATUS_CLS[r.status]}`}>{r.status}</span></td>
                <td className="p-2 text-xs text-zinc-400">{r.created_at ? new Date(r.created_at).toLocaleDateString("it-IT") : "—"}</td>
                <td className="p-2 text-xs text-zinc-400">{r.activated_at ? new Date(r.activated_at).toLocaleDateString("it-IT") : "—"}</td>
                <td className="p-2 max-w-[200px] truncate text-xs text-zinc-400">{r.description || "—"}</td>
                <td className="p-2 text-xs text-zinc-400">{r.created_by || "—"}</td>
                <td className="p-2">
                  <div className="flex flex-wrap gap-1">
                    <button data-testid={`algo-view-${r.name}`} onClick={() => setView(r)} className="rounded-full border border-white/10 px-2 py-1 text-xs">Visualizza</button>
                    {r.status === "DRAFT" && <button data-testid={`algo-edit-${r.name}`} onClick={() => setDraft({ ...r, params: { ...r.params } })} className="rounded-full border border-white/10 px-2 py-1 text-xs">Modifica</button>}
                    {r.status === "DRAFT" && <button data-testid={`algo-activate-${r.name}`} onClick={() => askActivate(r)} className="rounded-full bg-emerald-600 px-2 py-1 text-xs font-semibold">Attiva</button>}
                    {r.status === "DRAFT" && <button data-testid={`algo-archive-${r.name}`} onClick={() => doArchive(r)} className="rounded-full border border-white/10 px-2 py-1 text-xs text-rose-300">Archivia</button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className={card + " space-y-3"} data-testid="algo-compare">
        <p className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Confronta versioni</p>
        <div className="flex flex-wrap items-end gap-2">
          <Field label="Versione A"><select data-testid="algo-cmp-a" className={inp} value={cmp.a} onChange={(e) => setCmp({ ...cmp, a: e.target.value })}><option value="">—</option>{rows.map((r) => <option key={r.id} value={r.id}>{r.name} ({r.status})</option>)}</select></Field>
          <Field label="Versione B"><select data-testid="algo-cmp-b" className={inp} value={cmp.b} onChange={(e) => setCmp({ ...cmp, b: e.target.value })}><option value="">—</option>{rows.map((r) => <option key={r.id} value={r.id}>{r.name} ({r.status})</option>)}</select></Field>
          <button data-testid="algo-cmp-run" onClick={runCompare} className="rounded-full bg-[#2563EB] px-4 py-2 text-sm font-semibold">Confronta</button>
        </div>
        {cmp.res && <DiffTable res={cmp.res} />}
      </div>

      {creating && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setCreating(false)}>
          <div className="w-full max-w-md space-y-3 rounded-2xl border border-white/10 bg-[#0B0E13] p-6" onClick={(e) => e.stopPropagation()} data-testid="algo-create-modal">
            <p className="text-lg font-bold">Nuova versione (da {active?.name || "ACTIVE"})</p>
            <Field label="Nome versione (es. 2.1)"><input data-testid="algo-new-name" className={inp} value={nv.name} onChange={(e) => setNv({ ...nv, name: e.target.value })} /></Field>
            <Field label="Descrizione"><input data-testid="algo-new-desc" className={inp} value={nv.description} onChange={(e) => setNv({ ...nv, description: e.target.value })} /></Field>
            <div className="flex justify-end gap-2"><button onClick={() => setCreating(false)} className="rounded-full border border-white/10 px-4 py-2 text-sm">Annulla</button><button data-testid="algo-new-save" onClick={doCreate} className="rounded-full bg-[#2563EB] px-4 py-2 text-sm font-semibold">Crea bozza</button></div>
          </div>
        </div>
      )}

      {draft && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 p-4" onClick={() => setDraft(null)}>
          <div className="my-8 w-full max-w-lg space-y-3 rounded-2xl border border-white/10 bg-[#0B0E13] p-6" onClick={(e) => e.stopPropagation()} data-testid="algo-edit-modal">
            <p className="text-lg font-bold">Modifica bozza {draft.name}</p>
            <Field label="Descrizione"><input className={inp} value={draft.description || ""} onChange={(e) => setDraft({ ...draft, description: e.target.value })} /></Field>
            <div className="grid grid-cols-2 gap-2">
              {DRAFT_FIELDS.map(([k, label, step]) => (
                <Field key={k} label={label}><input data-testid={`algo-edit-${k}`} className={inp} type="number" step={step} value={draft.params?.[k] ?? ""} onChange={(e) => setDraft({ ...draft, params: { ...draft.params, [k]: e.target.value === "" ? null : parseFloat(e.target.value) } })} /></Field>
              ))}
            </div>
            <div className="flex justify-end gap-2"><button onClick={() => setDraft(null)} className="rounded-full border border-white/10 px-4 py-2 text-sm">Annulla</button><button data-testid="algo-edit-save" onClick={saveDraft} className="rounded-full bg-[#2563EB] px-4 py-2 text-sm font-semibold">Salva bozza</button></div>
          </div>
        </div>
      )}

      {view && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 p-4" onClick={() => setView(null)}>
          <div className="my-8 w-full max-w-lg space-y-3 rounded-2xl border border-white/10 bg-[#0B0E13] p-6" onClick={(e) => e.stopPropagation()} data-testid="algo-view-modal">
            <div className="flex items-center gap-2"><span className={`rounded-full px-2 py-0.5 text-xs ${ALGO_STATUS_CLS[view.status]}`}>{view.status}</span><p className="text-lg font-bold">{view.name}</p></div>
            {view.description && <p className="text-xs text-zinc-400">{view.description}</p>}
            <p className="text-[11px] text-zinc-500">Creato da {view.created_by || "—"} · {view.created_at ? new Date(view.created_at).toLocaleString("it-IT") : ""}{view.parent ? ` · derivata da ${view.parent}` : ""}</p>
            <div className="rounded-lg border border-white/5 bg-[#11141A] p-3">
              <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Parametri (snapshot)</p>
              <pre className="whitespace-pre-wrap break-all text-[11px] text-zinc-300">{JSON.stringify(view.params, null, 1)}</pre>
            </div>
            <div className="flex justify-end"><button onClick={() => setView(null)} className="rounded-full border border-white/10 px-4 py-2 text-sm">Chiudi</button></div>
          </div>
        </div>
      )}
      {activate && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 p-4" onClick={() => setActivate(null)}>
          <div className="my-8 w-full max-w-2xl space-y-3 rounded-2xl border border-white/10 bg-[#0B0E13] p-6" onClick={(e) => e.stopPropagation()} data-testid="algo-activate-modal">
            <p className="text-lg font-bold">Conferma attivazione versione algoritmo</p>
            <p className="text-xs text-zinc-400">Versione attuale ACTIVE <span className="font-semibold text-zinc-200">{activate.diff.a.name}</span> &nbsp;VS&nbsp; Nuova versione <span className="font-semibold text-zinc-200">{activate.diff.b.name}</span></p>
            <DiffTable res={activate.diff} testid="algo-activate-difftable" />
            <div className="rounded-lg border border-amber-500/25 bg-amber-500/10 p-3 text-sm text-amber-300" data-testid="algo-activate-warning">Questa operazione renderà la nuova versione ACTIVE e archivierà la versione attuale.</div>
            <div className="flex justify-end gap-2">
              <button data-testid="algo-activate-cancel" onClick={() => setActivate(null)} className="rounded-full border border-white/10 px-4 py-2 text-sm">ANNULLA</button>
              <button data-testid="algo-activate-confirm" onClick={confirmActivate} className="rounded-full bg-emerald-600 px-4 py-2 text-sm font-semibold">CONFERMA ATTIVAZIONE</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default function Valutazioni() {
  const [sub, setSub] = useState("agenda");
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {SUB.map(([id, label]) => (
          <button key={id} data-testid={`vsub-${id}`} onClick={() => setSub(id)} className={`rounded-full px-4 py-1.5 text-sm ${sub === id ? "bg-white text-[#0B0E13]" : "border border-white/10 text-zinc-300"}`}>{label}</button>
        ))}
      </div>
      {sub === "agenda" && <Agenda />}
      {sub === "simulatore" && <Simulatore />}
      {sub === "curva" && <CurvaSvalutazione />}
      {sub === "versioni" && <VersioniAlgoritmo />}
      {sub === "aggiornamenti" && <Aggiornamenti />}
      {sub === "config" && <Config />}
    </div>
  );
}
